/**
 * The PWA offline outbox for task mutations (§8.2, §8.6).
 *
 * Started as create-only (D154); broadened to also cover completing, deferring, editing
 * fields, and breaking a task into subtasks — the "core daily loop" the owner asked for,
 * scoped deliberately short of recurrence, notes, and area/project administration. See
 * DECISIONS.md D157 onward.
 *
 * **One shared, ordered queue for every kind of task op**, not one queue per action. A task
 * created offline and then, in the same offline stretch, completed or edited before ever
 * reconnecting has to have its create replayed *before* the later op — and since both share
 * one queue sorted by `queuedAt`, that ordering falls out for free rather than needing to be
 * built. The create's client-minted id (§5.1) is exactly what makes the later op able to
 * name that task at all before it has ever existed on the server.
 *
 * **No optimistic UI.** Making a queued action look instantly "done" — the struck-through
 * hold-in-place `useHeldTaskActions.ts` gives a *successful* completion — would mean
 * rebuilding that entire mechanism to work from a locally-guessed result instead of a real
 * response. Skipped on purpose: queue the write, say so with a notice (same pattern as §8.1
 * capture and §8.2 Add), and let the real state show up once the flush actually lands. Less
 * polished, considerably simpler, and honest about what has and hasn't happened yet.
 *
 * **Endpoints here are all already built**, unlike `/api/capture` — so unlike
 * `captureQueue.ts`, a failure that reaches the server and is refused (an `ApiError` that
 * isn't `isOffline` — e.g. deferring a task that was completed elsewhere before the queue
 * caught up) is given up on rather than retried forever, and reported with a notice.
 */

import {
  ApiError,
  api,
  request,
  setOfflineWriteQueue,
  type JustAddedBody,
  type Task,
  type TaskCreateBody,
} from "./api";
import { createOutbox, newUlid, outboxRecordBase, type OutboxRecord } from "./offlineOutbox";
import { postNotice } from "./notices";
import {
  mirrorAreaCreated,
  mirrorNoteCreated,
  mirrorRequest,
  mirrorInboxResolved,
  mirrorProjectCreated,
  mirrorSubtaskSplit,
  mirrorSubtasksCreated,
  mirrorTaskCompleted,
  mirrorTaskDeferred,
  mirrorTaskPatched,
} from "./offlineMirror";

export type TaskOpPayload =
  | { kind: "create"; payload: Omit<TaskCreateBody, "id"> }
  | { kind: "complete"; taskId: string }
  /** `until` is always a resolved `YYYY-MM-DD`, never left for the server to default — the
   * server's default is "tomorrow relative to whenever this request is actually processed",
   * which is wrong for a request that might not replay until days later (see `queueDefer`). */
  | { kind: "defer"; taskId: string; until: string }
  | { kind: "patch"; taskId: string; fields: Partial<Task> & { status?: Task["status"] } }
  /** Targets `/api/tasks/{id}/subtasks` — the same one control the UI already uses for both
   * "break this down" and "this step is too big" (§8.6.2). The server redirects to a split
   * when `taskId` already names a subtask; the queue does not need to know which case this
   * is any more than the online path does. */
  | { kind: "subtasks"; taskId: string; titles: string[]; ids: string[] }
  /**
   * A routine's occurrence (§8.8). `completedDate` is pinned at tap time for the same reason
   * `defer`'s `until` is: the server would otherwise stamp it from whenever the request is
   * finally processed, filing the work on the wrong day and taking `delta_days` and the
   * streak with it (D164).
   *
   * Replay-safe, verified against `services/occurrences.py` rather than assumed:
   * `_reclaim_latest_resolved` deletes the already-advanced pending row before reverting, so
   * a repeat lands back in the same place instead of skipping a cycle, and `services/wins.py`
   * fingerprints occurrence events by `(task, due_date)`, so a repeat cannot double-count as
   * a win. A replay that arrives after the *next* occurrence was already resolved is refused
   * by the server, which the give-up path reports.
   */
  | { kind: "occurrence-complete"; taskId: string; dueDate: string; completedDate: string }
  | { kind: "occurrence-skip"; taskId: string; dueDate: string }
  /**
   * Structure and inbox filing (§4, §7.2). Each carries the client-minted id of whatever it
   * creates (§5.1), which is both what makes a replay idempotent and what lets the mirror
   * show the new area/project/note immediately under the id the server will eventually use.
   */
  | { kind: "area-create"; id: string; name: string }
  | { kind: "project-create"; id: string; name: string; areaId: string }
  | { kind: "inbox-resolve"; itemId: string; body: InboxResolveBody }
  /**
   * Any other write that is safe to replay verbatim — `PATCH` setting fields, `DELETE`,
   * reordering, setting the Next Action or a recurrence rule. Opted in per endpoint with
   * `queueable: true` in `api.ts`, which is where the "must be idempotent" judgement is made
   * and explained; this just carries the request until it can be sent.
   *
   * A single generic kind rather than one per endpoint on purpose: these all replay the same
   * way, so a case each would be a dozen near-identical branches that could only differ by
   * being wrong.
   */
  | { kind: "request"; requestMethod: string; path: string; requestBody?: unknown }
  | { kind: "note-create"; id: string; note: NoteDraft }
  /** An Add box entry under an AI chip (D222). `id` is the entry's own, so a replay is the same
   * entry, and it is the id `GET /api/just-added` lists it under. */
  // Stored as "inbox-add" from before the entry had a front door of its own (D224); renaming it
  // would strand any record queued under the old name.
  | { kind: "inbox-add"; id: string; add: Omit<JustAddedBody, "id"> };

export type QueuedTaskOp = OutboxRecord & TaskOpPayload;

/**
 * A record this build cannot dispatch. Thrown rather than returned, and deliberately *not*
 * an `ApiError`, so `shouldRetry` below keeps it queued instead of giving up on it: a record
 * we don't understand is not a record we're entitled to delete.
 *
 * This exists because the alternative, discovered the hard way, is silent data loss —
 * `send`'s `switch` had no `default`, so an unrecognised `kind` fell through, returned
 * `undefined`, and `runFlush` deleted the record as successfully delivered without ever
 * making a request. See DECISIONS.md D160.
 */
class UnsendableRecord extends Error {}

/** Fires after a flush pass that actually delivered something, so whatever is on screen can
 * refetch. Listened to by `useQueueDelivered`, which every page's refresh counter uses. */
export const TASK_QUEUE_DELIVERED_EVENT = "efss:task-queue-delivered";

/**
 * What a discarded change *was*, in the reader's terms — the only trace left of it once
 * `onGiveUp` deletes it, so it has to carry enough to act on. A create says its title
 * verbatim, because retyping it is the entire recovery path; the rest name the operation,
 * since the task itself still exists and is still on screen to try again on.
 *
 * The `default` is not dead code: a record written by a *newer* build than the one running
 * (a stale tab after a deploy) reaches here with a `kind` this switch has never heard of, and
 * "an unrecognised change" is a great deal more use than the `undefined` it printed before.
 */
function describe(op: QueuedTaskOp): string {
  switch (op.kind) {
    case "create":
      return `adding "${op.payload.title}"`;
    case "complete":
      return "completing a task";
    case "defer":
      return "snoozing a task";
    case "patch":
      return "editing a task";
    case "subtasks":
      return `breaking a task into steps ("${op.titles.join('", "')}")`;
    case "occurrence-complete":
      return "completing a routine";
    case "occurrence-skip":
      return "skipping a routine";
    case "area-create":
      return `adding the area "${op.name}"`;
    case "project-create":
      return `adding the project "${op.name}"`;
    case "inbox-resolve":
      return op.body.action === "discard"
        ? "discarding an inbox item"
        : `filing an inbox item as a ${op.body.action}`;
    case "request":
      return describeRequest(op.requestMethod, op.path);
    case "note-create":
      return `adding the note "${op.note.title}"`;
    case "inbox-add":
      return `adding "${op.add.text}" to Just added`;
    default:
      return "an unrecognised change";
  }
}

const outbox = createOutbox<QueuedTaskOp>({
  dbName: "efss-task-create-queue",
  storeName: "queue",
  send: (record) => {
    switch (record.kind) {
      case "create": {
        const { payload } = record;
        return api.createTask({ ...payload, id: record.idempotencyKey });
      }
      case "complete":
        return api.completeTask(record.taskId);
      case "defer":
        return api.deferTask(record.taskId, record.until);
      case "patch":
        return api.patchTask(record.taskId, record.fields);
      case "subtasks":
        return api.createSubtasks(record.taskId, record.titles, record.ids);
      case "occurrence-complete":
        return api.completeOccurrence(
          record.taskId,
          record.dueDate,
          undefined,
          record.completedDate,
        );
      case "occurrence-skip":
        return api.skipOccurrence(record.taskId, record.dueDate);
      case "area-create":
        return api.createArea({ name: record.name, id: record.id });
      case "project-create":
        return api.createProject({ name: record.name, area_id: record.areaId, id: record.id });
      case "inbox-resolve":
        return api.resolveInbox(record.itemId, record.body);
      case "request":
        return request(record.path, {
          method: record.requestMethod,
          body: record.requestBody,
        });
      case "note-create":
        return api.createNote({ ...record.note, id: record.id });
      case "inbox-add":
        return api.justAddedAdd({ ...record.add, id: record.id });
      default:
        // Never fall through: an unhandled kind must fail loudly and stay queued, not be
        // deleted as if it had been sent (D160 — this switch silently destroyed queued work
        // before it had a `default`). The live case is a record written by a *newer* build
        // than the one running: a tab left open across a deploy.
        throw new UnsendableRecord(
          `This copy of eFss does not understand a queued change (${String(
            (record as { kind?: unknown }).kind,
          )}). It has been kept, not discarded - reload to pick up the current version.`,
        );
    }
  },
  // Every endpoint above already exists and always has — unlike capture, a rejection here
  // means the request genuinely can't succeed, not "try again once this ships" (D157).
  // `UnsendableRecord` is deliberately excluded from that reasoning: it means *this build*
  // can't send the record, which a reload may well fix, so it is kept rather than given up on.
  shouldRetry: (err) =>
    err instanceof UnsendableRecord || (err instanceof ApiError && err.isOffline),
  onGiveUp: (record, err) => {
    const message = err instanceof Error ? err.message : String(err);
    postNotice(`A queued change (${describe(record)}) couldn't be applied: ${message}`);
  },
  onDelivered: (result) => {
    // A flush almost always finishes *after* the cards have rendered — it starts on app
    // mount and on reconnect, neither of which is triggered by anything the reader did. Say
    // so, and tell the pages to refetch, or queued work lands on the server invisibly and
    // reads as "nothing happened" until the next manual reload (D160).
    postNotice(
      result.flushed === 1
        ? "1 queued change has been sent."
        : `${result.flushed} queued changes have been sent.`,
    );
    window.dispatchEvent(new Event(TASK_QUEUE_DELIVERED_EVENT));
  },
});

/**
 * Persist a task-creation payload — the same durability contract as `captureQueue.ts`'s
 * `queueCapture`. Whatever `QuickAddCard` would otherwise have sent to `api.createTask` is
 * what gets stored here verbatim, so replay sends the identical request.
 *
 * `readyAt`, when given, holds the record back from actually sending until that time even
 * though it is durably queued from the moment this returns — the inbox-bound branch of the
 * Add card uses it to let a precise GPS fix catch up with a capture, without making the
 * fastest path in (§8.2) wait on it. See `releaseQueuedTaskForSend`, which is what ends the
 * wait, and `position.ts` for why the wait exists at all.
 */
export async function queueTaskCreate(
  payload: Omit<TaskCreateBody, "id">,
  readyAt?: number,
): Promise<QueuedTaskOp> {
  const record: QueuedTaskOp = {
    ...outboxRecordBase(),
    kind: "create",
    payload: { ...payload, title: payload.title.trim() },
    ...(readyAt ? { readyAt } : {}),
  };
  await outbox.enqueue(record);
  return record;
}

/**
 * End a `queueTaskCreate(..., readyAt)` wait, with whatever position did or didn't arrive.
 *
 * A no-op if the record is already gone — sent while this was waiting (shouldn't happen
 * before `readyAt`, but a manual flush or a second tab racing this one costs nothing to
 * guard against) or never queued at all. `enqueue`'s `put` semantics upsert on
 * `idempotencyKey`, so writing the same key back with `readyAt` cleared is what makes this
 * record eligible on the very next flush, which the write already triggers.
 */
export async function releaseQueuedTaskForSend(
  idempotencyKey: string,
  position?: { lat: number; lon: number },
): Promise<void> {
  const record = await outbox.get(idempotencyKey);
  if (!record || record.kind !== "create") return;
  await outbox.enqueue({
    ...record,
    readyAt: undefined,
    payload: position ? { ...record.payload, ...position } : record.payload,
  });
}

export async function queueTaskComplete(taskId: string): Promise<QueuedTaskOp> {
  const record: QueuedTaskOp = { ...outboxRecordBase(), kind: "complete", taskId };
  await outbox.enqueue(record);
  // Show it, don't just remember it (D166). Every `queueTask*` below does the same: the
  // mirror is patched the way the server would have, so the cards re-read it and offline
  // looks like online. The next successful read replaces the guess with the truth.
  mirrorTaskCompleted(taskId);
  return record;
}

/** Matches `backend/efss/services/tasks.py`'s `DEFAULT_SNOOZE_DAYS = 1` — kept in sync by
 * hand since the frontend has no way to ask the server "what would you default to". If that
 * constant ever changes, this needs to change with it. */
function tomorrowLocalDate(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * `until` is resolved to a concrete date *now*, at queue time, even when the caller didn't
 * give one — never left for `send` to omit and the server to default at whatever later
 * moment the flush actually happens (§8.2 SPEC note; found while designing this queue, not
 * by a bug report: a defer snoozed with no explicit date, replayed two days after being
 * tapped, would otherwise land two days later than the owner meant by "tomorrow").
 */
export async function queueTaskDefer(taskId: string, until?: string): Promise<QueuedTaskOp> {
  const record: QueuedTaskOp = {
    ...outboxRecordBase(),
    kind: "defer",
    taskId,
    until: until ?? tomorrowLocalDate(),
  };
  await outbox.enqueue(record);
  mirrorTaskDeferred(taskId, record.kind === "defer" ? record.until : "");
  return record;
}

/** Recurrence changes are not accepted here (§8.2) — they're a second, chained API call the
 * outbox doesn't replay in sequence, same reasoning as `create`'s Add-with-Repeat refusal.
 * The caller (`useHeldTaskActions.ts`, `taskActions.ts`) is responsible for refusing that
 * combination before ever calling this. */
export async function queueTaskPatch(
  taskId: string,
  fields: Partial<Task> & { status?: Task["status"] },
): Promise<QueuedTaskOp> {
  const record: QueuedTaskOp = { ...outboxRecordBase(), kind: "patch", taskId, fields };
  await outbox.enqueue(record);
  mirrorTaskPatched(taskId, fields);
  return record;
}

/**
 * A subtask as it will exist once the server creates it, built locally so the fold can render
 * it now. The id is the one the queue will actually send (§5.1), so when the real row arrives
 * it replaces this one rather than joining it. The inherited fields follow §8.6: area and
 * project come from the parent and cannot diverge; energy is left unset for the reader.
 */
function provisionalSubtask(id: string, parent: Task, title: string): Task {
  const now = Math.floor(Date.now() / 1000);
  return {
    id,
    title,
    notes: null,
    status: "open",
    area_id: parent.area_id,
    project_id: parent.project_id,
    parent_id: parent.id,
    sort_order: (parent.subtasks?.length ?? 0) + 1,
    is_next_action: false,
    energy: null,
    important: false,
    starred: false,
    estimate_min: null,
    due: null,
    snoozed_until: null,
    rrule: null,
    recur_anchor: null,
    recur_start: null,
    created_at: now,
    updated_at: now,
    completed_at: null,
    last_touched_at: now,
    surfaced_count: 0,
    deferred_count: 0,
    starred_count: 0,
  };
}

export async function queueTaskSubtasks(
  parent: Task,
  titles: string[],
): Promise<QueuedTaskOp> {
  const taskId = parent.id;
  const record: QueuedTaskOp = {
    ...outboxRecordBase(),
    kind: "subtasks",
    taskId,
    titles,
    ids: titles.map(() => newUlid()),
  };
  await outbox.enqueue(record);
  if (record.kind === "subtasks") {
    const steps = record.titles.map((title, i) => provisionalSubtask(record.ids[i], parent, title));
    // The server dispatches on whether the target already has a parent (§8.6.2) — so does
    // this. A split *replaces* the subtask it was invoked on; a breakdown appends to a
    // parent. One control, two outcomes, mirrored to match (D169).
    if (parent.parent_id !== null) mirrorSubtaskSplit(parent.id, parent.parent_id, steps);
    else mirrorSubtasksCreated(taskId, steps);
  }
  return record;
}

/**
 * A routine's occurrence, completed offline (§8.8). `completedDate` is resolved here, at tap
 * time — see the type's own note for why the server's default is wrong for a replayed
 * request, and D164 for the same bug existing online across midnight.
 */
export async function queueOccurrenceComplete(
  taskId: string,
  dueDate: string,
  completedDate: string,
): Promise<QueuedTaskOp> {
  const record: QueuedTaskOp = {
    ...outboxRecordBase(),
    kind: "occurrence-complete",
    taskId,
    dueDate,
    completedDate,
  };
  await outbox.enqueue(record);
  return record;
}

export async function queueOccurrenceSkip(taskId: string, dueDate: string): Promise<QueuedTaskOp> {
  const record: QueuedTaskOp = { ...outboxRecordBase(), kind: "occurrence-skip", taskId, dueDate };
  await outbox.enqueue(record);
  return record;
}

/** Whatever `POST /api/inbox/{id}/resolve` accepts — carried verbatim so a replay sends the
 * identical request, including the client-minted `id` that makes it idempotent (§5.1). */
export type InboxResolveBody = {
  action: "task" | "area" | "project" | "note" | "context" | "discard";
  id?: string;
  title?: string;
  area_id?: string | null;
  project_id?: string | null;
  notes?: string | null;
  energy?: number | null;
  important?: boolean;
  starred?: boolean;
  estimate_min?: number | null;
  due?: string | null;
};

export type NoteDraft = {
  title: string;
  body?: string | null;
  area_id?: string | null;
  project_id?: string | null;
};

export async function queueNoteCreate(note: NoteDraft): Promise<QueuedTaskOp> {
  const record: QueuedTaskOp = {
    ...outboxRecordBase(),
    kind: "note-create",
    id: newUlid(),
    note,
  };
  await outbox.enqueue(record);
  if (record.kind === "note-create") mirrorNoteCreated(record.id, note);
  return record;
}

/** Hold an Add box entry until it can be sent. Returns the record, whose `id` names the Just added
 * entry it becomes. */
export async function queueJustAdded(add: Omit<JustAddedBody, "id">): Promise<{ id: string }> {
  const id = newUlid();
  await outbox.enqueue({ ...outboxRecordBase(), kind: "inbox-add", id, add: { ...add, text: add.text.trim() } });
  return { id };
}

export async function queueAreaCreate(name: string): Promise<QueuedTaskOp> {
  const record: QueuedTaskOp = { ...outboxRecordBase(), kind: "area-create", id: newUlid(), name };
  await outbox.enqueue(record);
  if (record.kind === "area-create") mirrorAreaCreated(record.id, name);
  return record;
}

export async function queueProjectCreate(name: string, areaId: string): Promise<QueuedTaskOp> {
  const record: QueuedTaskOp = {
    ...outboxRecordBase(),
    kind: "project-create",
    id: newUlid(),
    name,
    areaId,
  };
  await outbox.enqueue(record);
  if (record.kind === "project-create") mirrorProjectCreated(record.id, name, areaId);
  return record;
}

/**
 * An inbox item filed offline. The caller supplies the body it would have sent online; the
 * client-minted id for whatever it becomes is added here, so one place owns that rule.
 */
export async function queueInboxResolve(
  itemId: string,
  body: Omit<InboxResolveBody, "id">,
): Promise<QueuedTaskOp> {
  const resolved: InboxResolveBody =
    body.action === "discard" ? { ...body } : { ...body, id: newUlid() };
  const record: QueuedTaskOp = {
    ...outboxRecordBase(),
    kind: "inbox-resolve",
    itemId,
    body: resolved,
  };
  await outbox.enqueue(record);
  mirrorInboxResolved(itemId, resolved);
  return record;
}

/** A queued generic write, in the reader's terms — enough to recognise what was lost if it
 * ever has to be discarded, without a phrase per endpoint. */
function describeRequest(method: string, path: string): string {
  const what = path.includes("/areas")
    ? "an area"
    : path.includes("/projects")
      ? "a project"
      : path.includes("/notes")
        ? "a note"
        : path.includes("/occurrences")
          ? "a routine"
          : "a task";
  if (method === "DELETE") return `deleting ${what}`;
  if (path.endsWith("/order")) return `reordering ${what === "an area" ? "areas" : "steps"}`;
  if (path.endsWith("/recurrence")) return "changing a repeat rule";
  if (path.endsWith("/next-action")) return "setting the next step";
  if (path.endsWith("/complete")) return `completing ${what}`;
  return `updating ${what}`;
}

/**
 * Park a write that could not reach the server, and reflect it locally so the screen agrees
 * with what was just done (§8.2).
 *
 * Registered with `api.ts` at import time — it calls this rather than importing the queue,
 * which would be a cycle. The returned value stands in for the response the caller expected;
 * it is never read for anything but rendering, and the next successful read replaces it.
 */
setOfflineWriteQueue(async ({ method, path, body }) => {
  const record: QueuedTaskOp = {
    ...outboxRecordBase(),
    kind: "request",
    requestMethod: method,
    path,
    requestBody: body,
  };
  await outbox.enqueue(record);
  return mirrorRequest(method, path, body);
});

export const flushTaskQueue = outbox.flush;
/** For a "N changes waiting to send" indicator, whenever something wants to show one. */
export const pendingTaskCount = outbox.pendingCount;
export const startTaskQueueFlushing = outbox.startFlushing;
